/**
 * Translation Service
 *
 * High-level service for working with translations in the application.
 * Wraps @ngx-translate and adds application-specific functionality.
 *
 * Translations come from the static JSON bundle (`assets/i18n/{lang}.json`)
 * via `TranslationLoaderService` — the single source of truth. This service
 * only deals with WHICH language is active and how the user's choice is
 * persisted; it never fetches translation content itself.
 *
 * ## Usage
 *
 * ### Basic Translation
 * ```typescript
 * constructor(private i18n: TranslationService) {}
 *
 * // Get translation
 * const text = this.i18n.instant('shared.actions.save');
 *
 * // With parameters
 * const text = this.i18n.instant('shared.table.selected', { count: 5 });
 * ```
 *
 * ### Change Language
 * ```typescript
 * // Switch language (persists to user profile)
 * await this.i18n.setLanguage('fr');
 *
 * // Get current language
 * const lang = this.i18n.currentLanguage;
 * ```
 *
 * ### Reactive Usage
 * ```typescript
 * // Listen for language changes
 * this.i18n.onLanguageChange$.subscribe(lang => {
 *   console.log('Language changed to:', lang);
 * });
 * ```
 */

import { Injectable, inject, ApplicationRef, NgZone, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';

import {
  SupportedLanguage} from './language.config';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  isLanguageSupported,
  getLanguageConfig,
  getBrowserLanguage
} from './language.config';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private translate = inject(TranslateService);
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private appRef = inject(ApplicationRef);
  private ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  /** Current language subject */
  private currentLang$ = new BehaviorSubject<SupportedLanguage>(DEFAULT_LANGUAGE);

  /** Whether service has been initialized */
  private initialized = false;

  /** Whether initialization is in progress */
  private initializing = false;

  /** localStorage key for language preference */
  private readonly LANGUAGE_STORAGE_KEY = 'proporio_language';

  /** Observable of current language */
  get onLanguageChange$(): Observable<SupportedLanguage> {
    return this.currentLang$.asObservable();
  }

  /** Current language code */
  get currentLanguage(): SupportedLanguage {
    return this.currentLang$.value;
  }

  /** Current language configuration */
  get currentLanguageConfig() {
    return getLanguageConfig(this.currentLanguage);
  }

  /** All supported languages */
  get supportedLanguages() {
    return SUPPORTED_LANGUAGES;
  }

  /**
   * Initialize the translation service
   * Should be called on app startup, typically from AppComponent
   */
  async initialize(userLanguage?: string): Promise<void> {

    if (this.initialized) {
      return;
    }

    if (this.initializing) {
      return;
    }

    this.initializing = true;

    // Determine initial language (priority: user preference > browser > default)
    let language: SupportedLanguage;

    if (userLanguage && isLanguageSupported(userLanguage)) {
      language = userLanguage;
    } else {
      // Check localStorage before falling back to browser language
      const storedLang = this.getStoredLanguage();
      if (storedLang) {
        language = storedLang;
      } else {
        language = getBrowserLanguage();
      }
    }

    // Set up ngx-translate
    this.translate.setFallbackLang(DEFAULT_LANGUAGE);
    this.translate.addLangs(SUPPORTED_LANGUAGES.map(l => l.code));

    // Use the determined language
    await this.useLanguage(language);

    this.initialized = true;
    this.initializing = false;
  }

  /**
   * Change the current language
   * Persists to user profile if logged in
   */
  async setLanguage(language: SupportedLanguage): Promise<void> {
    if (!isLanguageSupported(language)) {
      console.warn(`[i18n] Unsupported language: ${language}`);
      return;
    }

    await this.useLanguage(language);

    // Store in localStorage for instant access on next page load
    this.storeLanguageLocally(language);

    // Persist to user profile if logged in
    await this.persistLanguagePreference(language);
  }

  /**
   * Apply a language locally without persisting it back to the user profile.
   *
   * Used when the source-of-truth already has the value — e.g. the
   * `users/{uid}.preferences.language` Firestore snapshot fan-out. Calling
   * `setLanguage` from that path causes a write-loop: the snapshot
   * fires the effect, the effect re-writes the doc, the doc fan-outs
   * back, etc. Especially nasty when two rapid clicks have writes still in
   * flight — the lagging snapshot flips the UI back and the system
   * oscillates until both writes settle.
   */
  async applyLanguage(language: SupportedLanguage): Promise<void> {
    if (!isLanguageSupported(language)) {
      console.warn(`[i18n] Unsupported language: ${language}`);
      return;
    }
    await this.useLanguage(language);
    this.storeLanguageLocally(language);
  }

  /**
   * Internal method to switch language without persistence
   */
  private async useLanguage(language: SupportedLanguage): Promise<void> {
    try {
      // Run the whole switch INSIDE the Angular zone. Some callers reach here
      // out-of-zone (e.g. the user-preferences path delivers the change via a
      // Firestore users/{uid} snapshot -> AuthStore signal -> app.component
      // effect -> applyLanguage), in which case the ngx-translate events and
      // the resulting signal writes would not schedule any change detection.
      // Wrapping in ngZone.run + an explicit, synchronous appRef.tick()
      // guarantees persistent OnPush views (notably the projected left
      // sidenav) refresh on a live switch without an F5.
      await this.ngZone.run(async () => {
        await firstValueFrom(this.translate.use(language));
        this.currentLang$.next(language);
        this.syncDocumentLanguage(language);
        this.appRef.tick();
      });
    } catch (error) {
      console.error(`[i18n] Failed to switch language to ${language}:`, error);
    }
  }

  /**
   * Keep `<html lang>` on the language actually being rendered.
   *
   * `index.html` ships `lang="en"` and nothing ever updated it, so a UI fully in
   * German still declared English and screen readers applied English
   * pronunciation rules to German, French and Dutch text — WCAG 3.1.1 Language
   * of Page. Set from `useLanguage`, the single choke point both `setLanguage`
   * and `applyLanguage` go through, so the boot path is covered too. It is also
   * what the browser consults for hyphenation, spellcheck and quote marks.
   *
   * Writes the full locale (`fr-BE`, not `fr`): it is valid BCP-47, it matches
   * what LOCALE_ID is given, and it lets a reader pick the regional voice.
   */
  private syncDocumentLanguage(language: SupportedLanguage): void {
    const locale = getLanguageConfig(language)?.locale ?? language;
    this.document.documentElement.setAttribute('lang', locale);
  }

  /**
   * Persist language preference to user profile
   */
  private async persistLanguagePreference(language: SupportedLanguage): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    try {
      const userRef = doc(this.firestore, 'users', user.uid);
      await updateDoc(userRef, {
        'preferences.language': language,
        updatedAt: new Date()
      });
    } catch (error) {
      console.warn('[i18n] Failed to save language preference:', error);
    }
  }

  /**
   * Get a translation synchronously
   * Only works if the translation has been loaded
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instant(key: string, params?: Record<string, any>): string {
    return this.translate.instant(key, params);
  }

  /**
   * Get a translation as an observable
   * Automatically updates when language changes
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(key: string, params?: Record<string, any>): Observable<string> {
    return this.translate.get(key, params);
  }

  /**
   * Get a translation as a promise
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getAsync(key: string, params?: Record<string, any>): Promise<string> {
    return firstValueFrom(this.translate.get(key, params));
  }

  /**
   * Get multiple translations
   */
  getMany(keys: string[]): Observable<Record<string, string>> {
    return this.translate.get(keys);
  }

  /**
   * Stream a translation that updates on language change
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stream(key: string, params?: Record<string, any>): Observable<string> {
    return this.translate.stream(key, params);
  }

  /**
   * Helper: Format a date according to current locale
   */
  formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    const locale = this.currentLanguageConfig?.locale || 'fr-BE';
    return new Intl.DateTimeFormat(locale, options).format(date);
  }

  /**
   * Helper: Format a number according to current locale
   */
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    const locale = this.currentLanguageConfig?.locale || 'fr-BE';
    return new Intl.NumberFormat(locale, options).format(value);
  }

  /**
   * Helper: Format currency according to current locale
   */
  formatCurrency(value: number, currency = 'EUR'): string {
    const locale = this.currentLanguageConfig?.locale || 'fr-BE';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(value);
  }

  /** Read stored language from localStorage */
  private getStoredLanguage(): SupportedLanguage | null {
    try {
      const stored = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
      if (stored && isLanguageSupported(stored)) {
        return stored;
      }
    } catch {
      // localStorage may be unavailable
    }
    return null;
  }

  /** Write language preference to localStorage for instant access */
  private storeLanguageLocally(language: SupportedLanguage): void {
    try {
      localStorage.setItem(this.LANGUAGE_STORAGE_KEY, language);
    } catch {
      // localStorage may be unavailable
    }
  }
}
